import { apiClient } from './api';
import { Resume, UpdateResumeData, PaginatedResponse, PaginationParams } from '../types';

export interface DeleteResumeOptions {
  hardDelete?: boolean;
  force?: boolean;
}

export interface DeleteResumeResponse {
  message: string;
  deletionType: 'soft' | 'hard';
  usageCount?: number;
  forceDelete?: boolean;
  applicationsAffected?: number;
}

export interface DeleteResumeError {
  error: string;
  usageCount?: number;
  message?: string;
  canForceDelete?: boolean;
}

export interface CleanupResponse {
  message: string;
  cleanedUp: number;
  skipped: number;
  total: number;
  errors?: string[];
}

export interface DeletedResumeWithUsage extends Resume {
  usageCount: number;
}

export const resumesService = {
  // Get all resumes
  getResumes: async (params: PaginationParams = {}): Promise<PaginatedResponse<Resume>> => {
    const response = await apiClient.get('/api/resumes', { params });
    return {
      data: response.resumes,
      pagination: response.pagination
    };
  },

  // Get resume by ID
  getResume: async (id: string): Promise<Resume> => {
    const response = await apiClient.get(`/api/resumes/${id}`);
    return response.resume;
  },

  // Upload new resume
  uploadResume: async (formData: FormData): Promise<Resume> => {
    const response = await apiClient.upload('/api/resumes', formData);
    return response.resume;
  },

  // Update resume
  updateResume: async (id: string, resumeData: UpdateResumeData): Promise<Resume> => {
    const response = await apiClient.put(`/api/resumes/${id}`, resumeData);
    return response.resume;
  },

  // Delete resume (with soft/hard delete and force options)
  deleteResume: async (id: string, options: DeleteResumeOptions = {}): Promise<DeleteResumeResponse> => {
    const params: any = {};
    if (options.hardDelete) params.hardDelete = 'true';
    if (options.force) params.force = 'true';
    
    const response = await apiClient.delete(`/api/resumes/${id}`, { params });
    return {
      message: response.message ?? 'Resume deleted successfully',
      deletionType: response.deletionType ?? 'soft',
      usageCount: response.usageCount,
      forceDelete: response.forceDelete ?? false,
      applicationsAffected: response.applicationsAffected ?? 0
    };
  },

  // Get soft-deleted resumes
  getDeletedResumes: async (params: PaginationParams = {}): Promise<PaginatedResponse<DeletedResumeWithUsage>> => {
    const response = await apiClient.get('/api/resumes/deleted', { params });
    return {
      data: response.resumes,
      pagination: response.pagination
    };
  },

  // Restore soft-deleted resume
  restoreResume: async (id: string): Promise<Resume> => {
    const response = await apiClient.patch(`/api/resumes/${id}/restore`);
    return response.resume;
  },

  // Cleanup orphaned soft-deleted resumes
  cleanupOrphanedResumes: async (): Promise<CleanupResponse> => {
    const response = await apiClient.delete('/api/resumes/cleanup/orphaned');
    return {
      message: response.message ?? '',
      cleanedUp: response.cleanedUp,
      skipped: response.skipped,
      total: response.total,
      errors: response.errors
    };
  },

  // Download resume
  downloadResume: async (id: string): Promise<Blob> => {
    const response = await apiClient.download(`/api/resumes/${id}/download`);
    return response.data;
  },

  // Set default resume
  setDefaultResume: async (id: string): Promise<Resume> => {
    const response = await apiClient.patch(`/api/resumes/${id}/default`);
    return response.resume;
  },

  // Get default resume
  getDefaultResume: async (): Promise<Resume | null> => {
    const response = await apiClient.get('/api/resumes/default');
    return response.resume || null;
  },

  // Get resume statistics
  getResumeStats: async (): Promise<any> => {
    const response = await apiClient.get('/api/resumes/stats/summary');
    return response.stats;
  },
}; 